<template>
  <el-form
    ref="formRef"
    class="flex"
    require-asterisk-position="right"
    label-position="top"
    :model="formValues"
    :rules="rules"
  >
    <div class="oa-form-left">
      <el-form-item prop="idNoFrontUrl" label="身份证人像面">
        <card-image-upload
          :readonly="disabled"
          :image="formValues.idNoFrontUrl"
          :action="UploadImageUrls.idNoFront"
          :placeholder-image="ssnFrontImg"
          @on-success="(res: any) => {
            const { idNoFrontInfo, picResult } = res || {};

            formValues.idNoFrontUrl = picResult?.picUrl;
            formValues.legalName = idNoFrontInfo?.name;
            formValues.legalIdNo = idNoFrontInfo?.identNo;

            formRef?.validateField?.('legalIdNo');
            formRef?.validateField?.('legalName');
            formRef?.validateField?.('idNoFrontUrl');
          }"
        />
      </el-form-item>
      <el-form-item prop="legalName" :label="`${props.personLabel}姓名`">
        <el-input
          v-model="formValues.legalName"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="legalPhone" :label="`${props.personLabel}电话`">
        <el-input
          v-model="formValues.legalPhone"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
    </div>

    <div class="oa-form-right">
      <el-form-item prop="idNoBackUrl" label="身份证国徽面">
        <card-image-upload
          :readonly="disabled"
          :image="formValues.idNoBackUrl"
          :action="UploadImageUrls.idNoBack"
          :placeholder-image="ssnBackImg"
          @on-success="(res: any) => {
            const { idNoBackInfo, picResult } = res || {};

            formValues.idNoBackUrl = picResult?.picUrl;

            if (idNoBackInfo?.startDate && idNoBackInfo?.endDate) {
              formValues.idNoExpiryStartDate = idNoBackInfo.startDate.replace(/-/g, '');
              formValues.idNoExpiryEndDate = idNoBackInfo.endDate.replace(/-/g, '');
              formValues.idNoExpiryDate = [
                changeFormatToDate(formValues.idNoExpiryStartDate),
                changeFormatToDate(formValues.idNoExpiryEndDate),
              ];
              isLongTerm = longTimeValues.includes(idNoBackInfo?.endDate);
            }

            formRef?.validateField?.('idNoExpiryDate');
            formRef?.validateField?.('idNoBackUrl');
          }"
        />
      </el-form-item>
      <el-form-item prop="legalIdNo" :label="`${props.personLabel}证件号`">
        <el-input
          v-model="formValues.legalIdNo"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="idNoExpiryDate" label="证件有效期">
        <el-date-picker
          v-model="formValues.idNoExpiryDate"
          type="daterange"
          clearable
          start-placeholder="创建时间(起)"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          end-placeholder="创建时间(止)"
          :disabled="disabled"
          @change="isLongTerm = false"
        />
        <el-checkbox
          v-model="isLongTerm"
          class="ml-[24px]"
          label="长期"
          :disabled="isLongTerm || disabled"
          @change="changeLongTerm()"
        />
      </el-form-item>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { CardImageUpload } from '@vue3/components';
import { FormInstance, FormRules } from 'element-plus';
import { computed, PropType, ref, toRaw, watch } from 'vue';
import { ILeagalPerson } from './type';
import { changeFormatToDate, checkPhone, checkSSNId, getFileExt, scrollToErrorField, longTimeValues } from '@core/tools';
import ssnFrontImg from '@app/assets/images/ssn-front.png';
import ssnBackImg from '@app/assets/images/ssn-back.png';
import { UploadImageUrls } from '../../../../api/common';

const props = defineProps({
  data: {
    type: Object as PropType<ILeagalPerson>,
    default: () => {
      return {} as ILeagalPerson;
    },
  },
  disableAll: {
    type: Boolean,
    default: false,
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  /** 是否是个体工商户 */
  personLabel: {
    type: String,
    default: '',
  },
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  /** 法人姓名 */
  name: {
    type: String,
    default: '',
  },
  /**
   * 长期时间格式
   * @default 2999-12-31
   */
  longTimeFormat: {
    type: String,
    default: longTimeValues[2],
  },
});

const emit = defineEmits<{'key-enter': []}>();

const formRef = ref<FormInstance>();

const formValues = ref({} as ILeagalPerson);

const isLongTerm = ref(false);

const disabled = computed(() => props.disableAll);

const rules = computed<FormRules<ILeagalPerson>>(() => {
  return props.disableAll
    ? {}
    : {
      idNoFrontUrl: [
        {
          required: true,
          message: '请上传身份证人像面',
        },
      ],
      idNoBackUrl: [
        {
          required: true,
          message: '请上传身份证国徽面',
        },
      ],
      legalName: [
        {
          required: true,
          message: `请输入${props.personLabel}姓名`,
          trigger: 'blur',
        },
      ],
      legalIdNo: [
        {
          required: true,
          validator: (rule: any, value: string, callback: any) => {
            if (checkSSNId(formValues.value.legalIdNo)) {
              callback(checkSSNId(formValues.value.legalIdNo, props.personLabel));
            }
            callback();
          },
        },
      ],
      legalPhone: [
        {
          required: true,
          validator: (rule: any, value: string, callback: any) => {
            if (checkPhone(formValues.value.legalPhone)) {
              callback(checkPhone(formValues.value.legalPhone, props.personLabel));
            }
            callback();
          },
        },
      ],
      idNoExpiryDate: [
        {
          required: true,
          message: '请选择证件有效期',
        },
      ],
    };
});

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    } else {
      if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    }
  });

  return valid;
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const valid = await validate();

  if (!valid) {
    if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    return;
  }

  emit('key-enter');
}

function changeLongTerm() {
  if (isLongTerm.value) {
    const time = formValues.value.idNoExpiryDate;

    formValues.value.idNoExpiryDate = [!time || !time?.[0] ? '1950-01-01' : time[0], props.longTimeFormat];
    formRef.value?.validateField('idNoExpiryDate');
  }
}

function resetFormValues() {
  const d = JSON.parse(JSON.stringify(props.data)) as ILeagalPerson;

  if (!d.legalIdNo) d.legalIdNo = d.idNo; // 兼容旧数据

  formRef.value?.resetFields?.();
  formValues.value = {
    idNoFrontUrl: d.idNoFrontUrl,
    idNoBackUrl: d.idNoBackUrl,
    legalName: props.name || d.legalName,
    legalIdNo: d.legalIdNo,
    legalPhone: d.legalPhone,
    idNoExpiryStartDate: d.idNoExpiryStartDate,
    idNoExpiryEndDate: d.idNoExpiryEndDate,
  };
}

watch(
  () => props.data,
  () => {
    resetFormValues();

    if (props.data.idNoExpiryStartDate && props.data.idNoExpiryEndDate) {
      formValues.value.idNoExpiryDate = [
        changeFormatToDate(props.data.idNoExpiryStartDate),
        changeFormatToDate(props.data.idNoExpiryEndDate),
      ];
    }
    if (longTimeValues.includes(props.data.idNoExpiryEndDate)) {
      isLongTerm.value = true;
    }
  },
  { immediate: true },
);

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => ({
    ...toRaw(formValues.value),
    idNoExpiryDate: undefined,
    idNoFrontType: getFileExt(formValues.value.idNoFrontUrl),
    idNoBackType: getFileExt(formValues.value.idNoBackUrl),
    idNoExpiryStartDate: formValues.value.idNoExpiryDate?.[0]?.replace(/-/g, ''),
    idNoExpiryEndDate: formValues.value.idNoExpiryDate?.[1]?.replace(/-/g, ''),
  }),
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});
</script>

<style scoped lang="scss" src="../index.scss" />

